import { logout } from "@ui/Auth/api";
import { useLocalstorageState } from "rooks";

import { useAuth } from "@ui/components/AuthProvider";
import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from "react";
import { ROUTES } from "utils/constants/route.constants";
import localStorageUtils, {
  localStorageKeys,
} from "utils/local-utils/localstorage.utils";
import { goToUrl } from "utils/window-utils/window-utils";

const initialContext = {
  user: {},
  isAuthenticated: false,
  isLoading: false,
  setIsLoading: (value) => {},
  mainClient: {},
  appLogout: () => {},
  setUserDetails: (userDetails) => {},
  setIsAuthenticated: (isAuthenticated) => {},
  userDetails: {},
  isMobileVerified: false,
  setIsMobileVerified: (isVerified) => {},
  setBasicStepFormVisited: (isBasicStepFormVisited) => {},
  basicStepFormVisited: false,
};

const AuthContext = createContext(initialContext);

export const getMainClient = () =>
  localStorageUtils.get(localStorageKeys.mainClient);

export const AuthProvider = ({ children }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isMobileVerified, setIsMobileVerified] = useLocalstorageState(
    localStorageKeys.isNumberVerified,
    false
  );
  const [basicStepFormVisited, setBasicStepFormVisited] = useLocalstorageState(
    localStorageKeys?.basicStepFormVisited,
    false
  );

  const auth = useAuth();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const mainClient = localStorageUtils?.get(localStorageKeys.mainClient) || {};

  const setUserDetails = (userDetails) => {
    localStorageUtils.set(localStorageKeys.userDetails, userDetails);
  };

  const appLogout = useCallback(async () => {
    await logout();
    await auth.signout();
    localStorageUtils.remove(localStorageKeys.token);
    localStorageUtils.remove(localStorageKeys.cartItems);
    localStorageUtils.remove(localStorageKeys.userDetails);
    setUserDetails({});
    setIsLoading(false);
    goToUrl(ROUTES.HOME);
  }, [auth]);

  useLayoutEffect(() => {
    const isTokenExists =
      localStorage?.getItem(localStorageKeys.token) !== "undefined"
        ? true
        : false;

    setIsAuthenticated(isTokenExists);
  }, [localStorage]);

  const authContextValues = useMemo(
    () => ({
      setIsMobileVerified,
      isMobileVerified,
      isAuthenticated,
      isLoading,
      setIsLoading,
      mainClient,
      appLogout,
      setUserDetails,
      setIsAuthenticated,
      userDetails: localStorageUtils?.get(localStorageKeys?.userDetails),
      setBasicStepFormVisited,
      basicStepFormVisited,
    }),
    [
      setBasicStepFormVisited,
      basicStepFormVisited,
      isAuthenticated,
      isLoading,
      mainClient,
      appLogout,
      setIsMobileVerified,
      isMobileVerified,
    ]
  );

  return (
    <AuthContext.Provider value={authContextValues}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAppAuth = () => useContext(AuthContext);
